import { ArrowDown, ArrowUpRight } from "lucide-react";
import { ICONO, Wordmark, useFavicon } from "./brand";
import { HeroControles, HeroFondo, useHero } from "./HeroCine";
import { Mapa3D } from "./InicioSecciones";
import { CifraVidrio, Revelar, useCifras, useParallax } from "./cine";
import "./portal-auth.css";
import "./tierrascore.css";

const CAPITULOS = [
  { id: "catastro", img: "soja", ojo: "01 · Catastro SNC", t: "Cada padrón, con su dueño y su forma exacta", s: "Cruzamos el Servicio Nacional de Catastro con el registro de titulares: superficie, finca, valor fiscal y contorno real de cada parcela rural." },
  { id: "satelite", img: "chaco", ojo: "02 · Satélite Sentinel-2", t: "Qué hay realmente dentro del campo", s: "Uso del suelo, área mecanizada, pastura, monte y agua; salud del cultivo y ciclo de siembra de cada campaña, medidos desde el espacio." },
  { id: "clima", img: "rio", ojo: "03 · Clima y riesgo", t: "Lluvia, tormenta y sequía, al día", s: "Clima actual en cada inmueble, lluvia acumulada y alertas de tormenta para toda tu cartera de garantías." },
  { id: "credito", img: "ganado", ojo: "04 · Del campo al crédito", t: "Informes listos para decidir", s: "Rinde estimado, capacidad de pago y PDF profesional por parcela o por titular, con la trazabilidad de cada dato." },
];

const Nav = () => <nav className="cn-nav" data-testid="landing-nav">
  <a href="#inicio" className="flex items-center gap-2" data-testid="landing-nav-marca"><img src={ICONO} alt="" className="w-8 h-8" /><Wordmark className="text-xl" /></a>
  <div className="hidden md:flex items-center gap-1">{[["#paraguay", "Paraguay"], ["#catastro", "Catastro"], ["#satelite", "Satélite"], ["#clima", "Clima"]].map(([h, l]) => <a key={h} href={h} className="cn-nav-link" data-testid={`landing-nav-${h.slice(1)}`}>{l}</a>)}</div>
  <a href="#acceso" className="cn-pill" data-testid="landing-nav-ingresar">Ingresar <ArrowUpRight className="w-4 h-4" /></a>
</nav>;

const Capitulo = ({ c, i }) => {
  const ref = useParallax();
  return <section id={c.id} ref={ref} className="cn-cap" data-testid={`landing-cap-${c.id}`}>
    <img src={`/paraguay/${c.img}.webp`} alt="" loading="lazy" className="cn-cap-img" />
    <div className={`cn-cap-scrim ${i % 2 ? "der" : ""}`} />
    <div className={`cn-cap-txt ${i % 2 ? "der" : ""}`}>
      <Revelar as="p" className="cn-ojo">{c.ojo}</Revelar>
      <Revelar as="h2" d={0.08} className="cn-h2">{c.t}</Revelar>
      <Revelar as="p" d={0.16} className="cn-p">{c.s}</Revelar>
    </div>
  </section>;
};

export function PortalShell({ titulo, sub, children }) {
  useFavicon();
  const h = useHero();
  const c = useCifras();
  return <div className="ts-login ts-hero cn-landing" data-testid="portal-shell">
    <section id="inicio" className="relative min-h-screen">
      <HeroFondo h={h} />
      <Nav />
      <div className="relative z-10 min-h-screen max-w-[1440px] mx-auto grid lg:grid-cols-[1.25fr_1fr] gap-10 lg:gap-16 px-5 pt-28 pb-10 sm:px-10 lg:px-16 items-center">
        <div className="flex flex-col gap-6 cn-hero-txt">
          <p className="cn-ojo ts-in">Agro paraguayo · Catastro + satélite</p>
          <h1 className="cn-h1 ts-in" data-testid="portal-tagline">Del espacio<br /><span>al crédito.</span></h1>
          <p className="ts-in text-sm md:text-base text-white/85 max-w-md" style={{ "--d": ".1s" }}>Catastro, satélite y registro para analizar garantías rurales en todo Paraguay.</p>
          <div className="grid grid-cols-3 gap-2 sm:gap-3 max-w-xl" data-testid="landing-cifras">
            <CifraVidrio valor={c?.titulares} etq="vínculos titular↔parcela" testid="landing-cifra-titulares" />
            <CifraVidrio valor={c?.parcelas} etq="parcelas en el índice" testid="landing-cifra-parcelas" d={0.08} />
            <CifraVidrio valor={c?.analisis_satelitales} etq="análisis satelitales" testid="landing-cifra-analisis" d={0.16} />
          </div>
          <HeroControles h={h} />
        </div>
        <div id="acceso" className="flex justify-center lg:justify-end scroll-mt-24">
          <div className="pa-card w-full max-w-md rounded-3xl p-6 sm:p-8 space-y-5" data-testid="portal-acceso">
            <div><p className="flex items-center gap-2 mb-2"><img src={ICONO} alt="" className="w-7 h-7" /><Wordmark className="text-lg" /></p>
              <h2 className="font-['Sora'] text-2xl sm:text-3xl font-bold tracking-tight text-[#13211b]" data-testid="portal-titulo">{titulo}</h2>
              {sub && <p className="mt-1 text-sm text-[#5f6f66]">{sub}</p>}</div>
            {children}
          </div>
        </div>
      </div>
      <a href="#paraguay" className="cn-bajar" aria-label="Seguir" data-testid="landing-bajar"><ArrowDown className="w-4 h-4" /></a>
    </section>
    <section id="paraguay" className="cn-py" data-testid="landing-paraguay">
      <div className="max-w-[1300px] mx-auto grid lg:grid-cols-2 gap-10 items-center px-5 sm:px-10 py-20 lg:py-28">
        <Revelar className="h-[340px] sm:h-[440px]"><Mapa3D /></Revelar>
        <div className="space-y-6">
          <Revelar as="p" className="cn-ojo">Cobertura nacional</Revelar>
          <Revelar as="h2" d={0.08} className="cn-h2">Paraguay entero,<br />padrón por padrón.</Revelar>
          <Revelar as="p" d={0.16} className="cn-p">El barrido catastral recorre el país sin parar e indexa cada titular rural de los 17 departamentos.</Revelar>
          <div className="grid grid-cols-3 gap-3">
            <CifraVidrio valor={c?.padrones_consultados} etq="padrones consultados" testid="landing-cifra-padrones" />
            <CifraVidrio valor={c?.zonas} etq="zonas en barrido" testid="landing-cifra-zonas" d={0.08} />
            <CifraVidrio valor={c?.departamentos} etq="departamentos" testid="landing-cifra-departamentos" d={0.16} />
          </div>
        </div>
      </div>
    </section>
    {CAPITULOS.map((x, i) => <Capitulo key={x.id} c={x} i={i} />)}
    <section className="cn-py text-center px-5 py-20" data-testid="landing-cierre">
      <Revelar as="h2" className="cn-h2 mx-auto max-w-3xl">Analizá tu próxima garantía rural hoy.</Revelar>
      <Revelar d={0.1} className="mt-6"><a href="#acceso" className="cn-pill grande" data-testid="landing-cierre-ingresar">Ingresar a TierraScore <ArrowUpRight className="w-5 h-5" /></a></Revelar>
      <p className="mt-10 text-[11px] text-white/45" data-testid="hero-creditos">Fotos de portada: Unsplash y Pexels (licencia libre) · Paisajes de capítulos: imágenes generadas con IA</p>
    </section>
  </div>;
}

export const Campo = ({ label, ...p }) => <label className="block text-sm space-y-1.5"><span className="text-[#5f6f66] font-medium">{label}</span><input {...p} className="pa-input" /></label>;
